/**
 * Performance Assessment Result Component
 *
 * Displays:
 * - Random IOPS (read, write, effective)
 * - Sequential throughput
 * - Write penalty
 * - Network bottleneck analysis
 */

import { Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Stack, Alert, Chip } from '@mui/material';
import type { PerformanceResults } from '../../types';
import { TooltipIcon } from '../ui';

// Network speeds in MB/s (1 Gb/s = 125 MB/s)
const NETWORK_SPEEDS = {
  '1 GbE': 125,
  '2.5 GbE': 312.5,
  '10 GbE': 1250,
} as const;

// 4K streaming bitrate (high-quality 4K: 50 Mbps = ~6.25 MB/s)
const BITRATE_4K_MBPS = 6.25;

interface PerformanceAssessmentProps {
  results: PerformanceResults;
}

export function PerformanceAssessment({ results }: PerformanceAssessmentProps) {
  // Calculate how many network standards the pool can saturate
  const readSpeedMBps = results.sequentialReadMBps;

  // Calculate 4K streams capacity (using read speed for streaming)
  const streams4K = Math.floor(readSpeedMBps / BITRATE_4K_MBPS);

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Performance Assessment
      </Typography>

      <Stack spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            Random IOPS <TooltipIcon tooltipKey="IOPS" />
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography>
              Read: <strong>{results.randomReadIOPS.toFixed(0)}</strong> <TooltipIcon tooltipKey="randomReadIOPS" />
            </Typography>
            <Typography>
              Write: <strong>{results.randomWriteIOPS.toFixed(0)}</strong> <TooltipIcon tooltipKey="randomWriteIOPS" />
            </Typography>
            <Typography>
              Effective: <strong>{results.effectiveIOPS.toFixed(0)}</strong> <TooltipIcon tooltipKey="effectiveIOPS" />
            </Typography>
          </Box>
        </Box>

        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            Sequential Throughput <TooltipIcon tooltipKey="sequentialThroughput" />
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography>
              Read: <strong>{results.sequentialReadMBps.toFixed(0)} MB/s</strong>
            </Typography>
            <Typography>
              Write: <strong>{results.sequentialWriteMBps.toFixed(0)} MB/s</strong>
            </Typography>
          </Box>
        </Box>

        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            Write Penalty: <strong>{results.writePenalty}×</strong> <TooltipIcon tooltipKey="writePenalty" />
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Each write requires {results.writePenalty} operations
          </Typography>
        </Box>
      </Stack>

      <Typography variant="subtitle1" gutterBottom sx={{ mt: 3, mb: 2 }}>
        Network Bottleneck Analysis
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        Most modern hard drives can saturate standard network connections. This compares your pool's expected throughput with common network standards.
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        <strong>Assumptions:</strong> Reading and transferring large files sequentially. Data is not yet in ARC cache (cache miss scenario). Transfer recipient can sustain the network speed.
      </Typography>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Network Standard</TableCell>
              <TableCell align="right">Max Speed</TableCell>
              <TableCell align="right">Pool Speed</TableCell>
              <TableCell align="right">Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(NETWORK_SPEEDS).map(([name, speed]) => {
              const canSaturate = readSpeedMBps >= speed;
              const utilizationPercent = Math.min((readSpeedMBps / speed) * 100, 100);
              return (
                <TableRow key={name}>
                  <TableCell>{name}</TableCell>
                  <TableCell align="right">{speed} MB/s</TableCell>
                  <TableCell align="right">{readSpeedMBps.toFixed(0)} MB/s</TableCell>
                  <TableCell align="right">
                    <Chip
                      label={canSaturate ? `Can saturate (${utilizationPercent.toFixed(0)}%)` : `Bottlenecked (${utilizationPercent.toFixed(0)}%)`}
                      color={canSaturate ? 'success' : 'warning'}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Alert severity="info" sx={{ mt: 2 }}>
        <strong>4K Streaming Capacity:</strong> This pool can sustain approximately <strong>{streams4K} simultaneous 4K streams</strong> (based on 50 Mbps per stream).
      </Alert>

      {results.bottleneck && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <strong>Storage Bottleneck:</strong> {results.bottleneck} <TooltipIcon tooltipKey="bottleneck" />
        </Alert>
      )}
    </Paper>
  );
}
